import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DirectionsWalkOutlinedIcon from '@mui/icons-material/DirectionsWalkOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import TerrainOutlinedIcon from '@mui/icons-material/TerrainOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import CategoryService from '../services/CategoryService';
import { useToast } from '../contexts/ToastContext';
import { STEP_KIND, workflowOf } from '../utils/workflows';

/** How each kind of checklist step is completed, shown as a small icon. */
const KIND_ICON = {
    DATE: <EventOutlinedIcon sx={{ fontSize: 16 }} />,
    CHECK: <CheckBoxOutlinedIcon sx={{ fontSize: 16 }} />,
    CHECK_PDF: <CheckBoxOutlinedIcon sx={{ fontSize: 16 }} />,
    PDF: <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} />,
    IMAGE: <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />,
    VISITS: <DirectionsWalkOutlinedIcon sx={{ fontSize: 16 }} />,
};

function Tag({ icon, children, color = '#427898', bg = 'rgba(66,120,152,0.10)' }) {
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.3, borderRadius: '7px', color, bgcolor: bg, fontSize: 11.5, fontWeight: 700 }}>
            {icon}{children}
        </Box>
    );
}

function CategoryCard({ category }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const wf = workflowOf(category);
    const color = category.color || '#64748B';
    return (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Header: colour, name, number of jobs (opens the filtered jobs list). */}
            <Box sx={{ p: 2.25, pb: 1.75, background: `linear-gradient(135deg, ${color}1A 0%, transparent 70%)`, borderBottom: '1px solid #F1F5F9' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '4px', bgcolor: color, mt: 0.6, flexShrink: 0 }} />
                    <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#0F172A', lineHeight: 1.35 }}>{category.name}</Typography>
                    <Box component="button" onClick={() => navigate(`/jobs?categoryId=${category.id}`)} sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, border: 0, cursor: 'pointer', flexShrink: 0,
                        px: 1, py: 0.4, borderRadius: '8px', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
                        fontSize: 12, fontWeight: 700, color: category.jobCount ? '#334155' : '#94A3B8', fontFamily: 'inherit',
                        '&:hover': { color: '#427898' },
                    }}>
                        <WorkOutlineIcon sx={{ fontSize: 14 }} />{t('categories.jobCount', { count: category.jobCount })}
                    </Box>
                </Box>
                {wf && (
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25, ml: 3.1 }}>
                        {wf.cadastre && <Tag icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 13 }} />} color="#B45309" bg="rgba(245,158,11,0.12)">{t('categories.cadastre')}</Tag>}
                        {wf.field && <Tag icon={<TerrainOutlinedIcon sx={{ fontSize: 13 }} />} color="#047857" bg="rgba(16,185,129,0.12)">{t('categories.fieldWork')}</Tag>}
                        {wf.investor && <Tag icon={<PersonOutlineIcon sx={{ fontSize: 13 }} />}>{t('jobs.fields.investor')}</Tag>}
                        {wf.parcels && <Tag icon={<GridOnOutlinedIcon sx={{ fontSize: 13 }} />}>{t('categories.parcelNumbers')}</Tag>}
                    </Box>
                )}
            </Box>
            {/* Checklist as a numbered timeline; the field step (what the planner plans) is marked. */}
            <Box sx={{ p: 2.25, pt: 1.75, flex: 1 }}>
                {wf ? wf.steps.map((code, i) => {
                    const last = i === wf.steps.length - 1;
                    const isField = code === wf.field;
                    const optional = code === 'PERMIT_PARCELLING';
                    return (
                        <Box key={code} sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                <Box sx={{
                                    width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 11.5, fontWeight: 800, color: isField ? '#fff' : color, bgcolor: isField ? '#059669' : `${color}1F`,
                                    border: optional ? `1.5px dashed ${color}` : 0,
                                }}>{i + 1}</Box>
                                {!last && <Box sx={{ width: 2, flex: 1, minHeight: 14, bgcolor: '#E2E8F0', my: 0.4 }} />}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: last ? 0 : 1.5, pt: 0.35, minWidth: 0, flex: 1 }}>
                                <Typography sx={{ flex: 1, fontSize: 13.5, color: '#1E293B', fontWeight: isField ? 700 : 500, lineHeight: 1.35 }}>
                                    {t(`steps.${code}`)}
                                    {isField && <Box component="span" sx={{ color: '#059669', fontWeight: 700 }}> · {t('categories.fieldStep')}</Box>}
                                    {optional && <Box component="span" sx={{ color: '#94A3B8', fontWeight: 500 }}> · {t('categories.optional')}</Box>}
                                </Typography>
                                <Tooltip title={t(`categories.kind.${STEP_KIND[code]}`)}>
                                    <Box sx={{ color: '#94A3B8', display: 'flex', mt: 0.1 }}>{KIND_ICON[STEP_KIND[code]]}</Box>
                                </Tooltip>
                            </Box>
                        </Box>
                    );
                }) : (
                    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{t('categories.plain')}</Typography>
                )}
            </Box>
        </Paper>
    );
}

/**
 * The firm's job categories and their checklists, read-only: they are fixed for now and will become configurable
 * (own categories and steps) later.
 */
export default function CategoriesPage() {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [categories, setCategories] = useState(null);

    useEffect(() => {
        CategoryService.list().then(setCategories).catch(() => showToast(t('common.loadError'), 'error'));
    }, [showToast, t]);

    return (
        <Layout>
            <PageHeader title={t('categories.title')} subtitle={t('categories.subtitle')} count={categories ? categories.length : null} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.75, mb: 2.5, borderRadius: '12px', bgcolor: 'rgba(66,120,152,0.06)', border: '1px solid rgba(66,120,152,0.14)' }}>
                <InfoOutlinedIcon sx={{ color: '#427898', fontSize: 20, mt: 0.1 }} />
                <Typography sx={{ fontSize: 13.5, color: '#334155' }}>{t('categories.fixedHint')}</Typography>
            </Box>
            {!categories ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>
            ) : (
                <Box sx={{
                    display: 'grid', gap: 2, alignItems: 'start',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                }}>
                    {categories.map(c => <CategoryCard key={c.id} category={c} />)}
                </Box>
            )}
        </Layout>
    );
}
